<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { formatMoneyFromPence } from '@/helpers/moneyDisplay'
import ClaimableTotalHint from '@/components/dashboard/claim/ClaimableTotalHint.vue'

/**
 * Live claim snapshot used while building the claim and again once it is
 * confirmed. Rows, not cards: this panel is only 640px wide, so three
 * metric cards clip or wrap as soon as giving reaches five or six figures.
 */
defineProps<{
  yearLabel: string
  givenDisplay: string
  claimablePence: number | null
  showClaimableAmount: boolean
  donationCount: number
}>()

const { t } = useI18n()
</script>

<template>
  <dl
    class="sa-claim-guide__metrics"
    :aria-label="t('Dashboard.ClaimTaxBack.Guide.Metrics.Aria', { year: yearLabel })"
  >
    <div class="sa-claim-guide__metric">
      <dt>{{ t('Dashboard.ClaimTaxBack.Guide.Metrics.Given') }}</dt>
      <dd>{{ givenDisplay }}</dd>
    </div>
    <div
      class="sa-claim-guide__metric"
      :class="{ 'sa-claim-guide__metric--claimable': showClaimableAmount }"
    >
      <dt>
        {{ t('Dashboard.ClaimTaxBack.Guide.Metrics.Claimable') }}
        <span
          v-if="!showClaimableAmount"
          class="sa-claim-guide__metric-note"
        >
          {{ t('Dashboard.ClaimTaxBack.Guide.Metrics.ClaimablePending') }}
        </span>
      </dt>
      <dd>
        <!-- "Up to" stays with the figure. The estimate note sits under it. -->
        <ClaimableTotalHint hint="guide" :show-hint="showClaimableAmount">
          {{
            showClaimableAmount
              ? formatMoneyFromPence(claimablePence ?? 0)
              : t('Dashboard.ClaimTaxBack.Guide.Metrics.ClaimableNotYet')
          }}
        </ClaimableTotalHint>
      </dd>
    </div>
    <div class="sa-claim-guide__metric">
      <dt>{{ t('Dashboard.ClaimTaxBack.Guide.Metrics.Count') }}</dt>
      <dd>{{ donationCount }}</dd>
    </div>
  </dl>
</template>
